import { useState } from "react";
import { useSelector } from "react-redux";
import Notification from "../components/Notification";
import { ChevronDown } from "lucide-react";
import { RiTimerLine } from "react-icons/ri";
import { IoMdArrowBack } from "react-icons/io";
import PaymentAmountCard from "../components/PaymentAmountCard";
import useTranslation from "../hooks/useTranslation";

const SUPPORTED_LANGUAGES = ["en", "id", "zh"];

const formatExpiryDate = (dateString, dateAtLabel) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(":", ".");

  return `${datePart} ${dateAtLabel} ${timePart}`;
};

function LanguageSwitcher() {
  const { t, language, setLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700"
      >
        {t(`languageNames.${language}`)}
        <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
            {SUPPORTED_LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => {
                  setLanguage(code);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center px-4 py-2 text-left text-sm ${
                  code === language ? "font-semibold text-singaHijauPrimary" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t(`languageNames.${code}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function PaymentLayout({ children, hideNavbar = false, unframedContent = false, onBack }) {
  const { t } = useTranslation();
  const notification = useSelector((state) => state.notification);
  const paymentData = useSelector((state) => state.payment.data);

  // ⭐ Ambil style dari Redux
  const { primary_color, primary_text_color, merchant_image, brand_name } = useSelector((state) => state.style);

  const merchantLogoSrc = merchant_image ? `${process.env.REACT_APP_API_URL}${merchant_image}` : null;

  return (
    <>
      {!hideNavbar && (
        <div
          className="fixed top-0 right-0 z-50 shadow-sm w-full py-1"
          style={{ background: primary_color || "white" }}
        >
          <div className="mx-auto flex h-14 w-full items-center justify-between px-4">
            <div className="flex min-w-0 items-center gap-4">
              {merchantLogoSrc && (
                <img src={merchantLogoSrc} alt="Merchant Logo" className="max-w-30 max-h-12 object-contain" />
              )}

              <span className="truncate text-base font-medium" style={{ color: primary_text_color }}>
                {brand_name}
              </span>
            </div>

            <LanguageSwitcher />
          </div>
        </div>
      )}

      <div
        className={`min-h-screen overflow-x-hidden flex items-start justify-center px-4 ${hideNavbar ? "py-10" : "pt-24 pb-10"}`}
      >
        <div className="relative z-10 mx-auto w-full md:max-w-[600px] flex flex-col gap-4">
          {onBack && (
            <div className="flex items-center gap-2 cursor-pointer" onClick={onBack}>
              <IoMdArrowBack className="font-medium text-2xl" />
              <p className="font-medium">{t("layout.back")}</p>
            </div>
          )}

          {/* Notifikasi */}
          <Notification message={notification.message} type={notification.type} title={notification.title} />

          {/* Expired at */}
          {paymentData?.expired_at && (
            <div className="flex gap-2 rounded-xl bg-red-200 px-4 py-3 text-red-500 items-center justify-center">
              <RiTimerLine className="h-4 w-4 shrink-0 mt-0" />
              <p className="text-sm font-normal leading-5">
                {t("layout.payBefore")}{" "}
                <span className="text-sm font-semibold leading-5">
                  {formatExpiryDate(paymentData.expired_at, t("layout.dateAt"))}
                </span>
              </p>
            </div>
          )}

          {paymentData && <PaymentAmountCard />}

          {/* Content */}
          {unframedContent ? (
            children
          ) : (
            <div className="overflow-hidden rounded-xl bg-white p-6 shadow-sm">{children}</div>
          )}

          <div className="mx-auto flex w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
            <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBy")}</span>
            <img
              src="/assets/images/logo-singapay-horizontal.png"
              alt="SingaPay"
              className="h-6 w-auto object-contain"
            />
            {t("layout.poweredBySuffix") && (
              <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBySuffix")}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
